import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'
import {
  CheckCircle, Loader2, ArrowLeft, DollarSign, Send
} from 'lucide-react'
import SEO from '@/components/layout/SEO'

const T = {
  navy: '#1a2740',
  navyMid: '#243452',
  sage: '#4a7c5e',
  sageDark: '#2d5a3d',
  sageLight: '#e8f2ec',
  cream: '#faf7f2',
  white: '#ffffff',
  gold: '#c9903a',
  slate: '#475569',
  muted: '#8a94a6',
  border: '#e2e8f0',
  error: '#dc2626',
}

const heroGrad = `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`
const sageGrad = `linear-gradient(135deg, ${T.sage}, ${T.sageDark})`

const TIER_COLORS = [T.sage, T.gold, '#8b5cf6', '#3b82f6']

const inputStyle: React.CSSProperties = {
  width: '100%', height: 50, padding: '0 14px',
  borderRadius: 12, border: `2px solid ${T.border}`,
  fontSize: 15, outline: 'none', background: T.white,
  color: T.navy, fontFamily: 'inherit', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 800, color: T.muted,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  display: 'block', marginBottom: 6,
}

const CHANNELS = [
  'blog', 'youtube', 'podcast', 'instagram', 'tiktok',
  'educator', 'church_leader', 'parent_community', 'other'
]

const PAYOUT_METHODS = ['paypal', 'bank_transfer', 'wise']

interface Tier {
  id: string
  tier_key: string
  label: string
  commission_rate: number
  min_referrals: number
}

export default function AffiliateApplyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const [tiers, setTiers] = useState<Tier[]>([])
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    website: '',
    promo_channel: '',
    payout_method: '',
    password: '',
    confirm_password: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [code, setCode] = useState('')
  const [joinStatus, setJoinStatus] = useState<'pending' | 'approved'>('pending')

  useEffect(() => {
    if (user) {
      setForm(p => ({
        ...p,
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
      }))
    }
  }, [user])

  useEffect(() => {
    supabase
      .from('affiliate_commission_tiers' as any)
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .then(({ data }) => { if (data && (data as any[]).length > 0) setTiers(data as unknown as Tier[]) })
  }, [])


  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user && (!form.full_name.trim() || !form.email.trim() || !form.password.trim())) {
      setError(t('affiliates.apply.errorRequired'))
      return
    }
    if (!user && form.password.length < 6) {
      setError(t('auth.signup.passwordTooShort', 'Password must be at least 6 characters'))
      return
    }
    if (!user && form.password !== form.confirm_password) {
      setError(t('auth.signup.passwordsMismatch', 'Passwords do not match'))
      return
    }
    if (!user && !form.promo_channel) {
      setError(t('affiliates.apply.errorChannel'))
      return
    }

    // Process website URL: if it doesn't start with http/https, prepend https://
    let processedWebsite = form.website.trim()
    if (processedWebsite && !/^https?:\/\//i.test(processedWebsite)) {
      processedWebsite = `https://${processedWebsite}`
    }

    setLoading(true)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('affiliate-apply', {
        body: {
          ...form,
          website: processedWebsite,
          referrer_url: window.location.href,
        },
      })

      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)

      setCode(data.code)
      if (data.status) setJoinStatus(data.status)
      setSubmitted(true)
    } catch (err: any) {
      setError(err.message || t('affiliates.apply.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{
            background: T.white, borderRadius: 28, padding: '56px 40px',
            maxWidth: 520, width: '100%', textAlign: 'center',
            boxShadow: '0 16px 60px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: T.sageLight, display: 'flex',
            alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
          }}>
            <CheckCircle size={36} color={T.sage} />
          </div>
          <h2 style={{ fontSize: 30, fontWeight: 900, color: T.navy, margin: '0 0 14px' }}>
            {joinStatus === 'approved' ? t('affiliates.apply.successApprovedTitle', 'You are in! 🎉') : t('affiliates.apply.successTitle')}
          </h2>
          <p style={{ fontSize: 16, color: T.slate, lineHeight: 1.7, margin: '0 0 28px' }}>
            {joinStatus === 'approved'
              ? t('affiliates.apply.successApprovedBody', 'Your affiliate account has been activated. You can now start earning.')
              : t('affiliates.apply.successBody')}
          </p>
          {code && (
            <div style={{
              background: T.sageLight, borderRadius: 14, padding: '20px 24px',
              marginBottom: 28, border: `1px solid ${T.sage}30`,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                {t('affiliates.apply.provisionalCode')}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: T.sageDark, letterSpacing: '0.06em' }}>{code}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                {t('affiliates.apply.codeNote')}
              </div>
            </div>
          )}
          <button
            onClick={() => navigate(joinStatus === 'approved' ? '/affiliates/dashboard' : '/')}
            style={{
              width: '100%', padding: '14px', borderRadius: 12,
              background: sageGrad, color: T.white,
              border: 'none', fontWeight: 800, fontSize: 16, cursor: 'pointer',
            }}
          >
            {joinStatus === 'approved' ? t('affiliates.apply.goToDashboard', 'Go to Dashboard') : t('affiliates.apply.backHome')}
          </button>
        </motion.div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: T.cream }}>
      <SEO
        title={t('affiliates.apply.title')}
        description={t('affiliates.apply.subtitle')}
        url="https://stillwatersacademy.com/affiliates/apply"
      />

      {/* Header */}
      <div style={{ background: heroGrad }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 24px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 24 }}>
            <Link to="/affiliates" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 14 }}>
              <ArrowLeft size={15} /> {t('affiliates.apply.back')}
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(74,124,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={24} color={T.sage} />
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: T.white, margin: 0 }}>{t('affiliates.apply.title')}</h1>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', margin: 0 }}>{t('affiliates.apply.subtitle')}</p>
            </div>
          </div>
        </div>
        <svg viewBox="0 0 1440 32" style={{ display: 'block', width: '100%', height: 32 }} preserveAspectRatio="none">
          <path d="M0,32 C360,0 1080,32 1440,12 L1440,32 Z" fill={T.cream} />
        </svg>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '16px 24px 80px' }}>

        {/* Commission summary — dynamic from DB */}
        {tiers.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 32 }}>
            {tiers.map((tier, i) => (
              <div key={tier.id} style={{
                background: T.white, borderRadius: 14,
                padding: '16px 18px', border: `1px solid ${T.border}`, textAlign: 'center',
              }}>
                <div style={{ fontSize: 26, fontWeight: 900, color: TIER_COLORS[i % TIER_COLORS.length] }}>
                  {tier.commission_rate}%
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{tier.label}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                  {tier.min_referrals === 0 ? 'Starting rate' : `${tier.min_referrals}+ referrals`}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          background: T.white, borderRadius: 24, padding: '36px 32px',
          border: `1px solid ${T.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
        }}>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: T.navy, margin: '0 0 28px' }}>
            {t('affiliates.apply.formTitle')}
          </h3>

          {!user && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>{t('affiliates.apply.fullName')} *</label>
                  <input value={form.full_name} onChange={set('full_name')} required
                    placeholder={t('affiliates.apply.fullNamePlaceholder')} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = T.sage)}
                    onBlur={e => (e.target.style.borderColor = T.border)} />
                </div>
                <div>
                  <label style={labelStyle}>{t('affiliates.apply.email')} *</label>
                  <input value={form.email} onChange={set('email')} required type="email"
                    placeholder={t('affiliates.apply.emailPlaceholder')} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = T.sage)}
                    onBlur={e => (e.target.style.borderColor = T.border)} />
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 6, lineHeight: 1.4 }}>
                    {t('affiliates.apply.existingAccountNote')}
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>{t('affiliates.apply.password')} *</label>
                  <input value={form.password} onChange={set('password')} required type="password"
                    placeholder={t('affiliates.apply.passwordPlaceholder')} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = T.sage)}
                    onBlur={e => (e.target.style.borderColor = T.border)} />
                </div>
                <div>
                  <label style={labelStyle}>{t('affiliates.apply.confirmPassword')} *</label>
                  <input value={form.confirm_password} onChange={set('confirm_password')} required type="password"
                    placeholder={t('affiliates.apply.confirmPasswordPlaceholder')} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = T.sage)}
                    onBlur={e => (e.target.style.borderColor = T.border)} />
                </div>
              </div>
            </>
          )}

          {user && (
            <div style={{ marginBottom: 24, padding: '16px 20px', background: T.sageLight, borderRadius: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckCircle size={20} color={T.sage} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{t('affiliates.apply.loggedAs', 'Applying as member:')} {user.email}</div>
                <div style={{ fontSize: 11, color: T.muted }}>{t('affiliates.apply.loggedNote', 'Your profile details will be auto-linked.')}</div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 18 }}>
            <div>
              <label style={labelStyle}>{t('affiliates.apply.website')}</label>
              <input value={form.website} onChange={set('website')} type="text"
                placeholder={t('affiliates.apply.websitePlaceholder')} style={inputStyle}
                onFocus={e => (e.target.style.borderColor = T.sage)}
                onBlur={e => (e.target.style.borderColor = T.border)} />
            </div>
            <div>
              <label style={labelStyle}>{t('affiliates.apply.channel')} {user ? '' : '*'}</label>
              <select value={form.promo_channel} onChange={set('promo_channel')} required={!user}
                style={{ ...inputStyle, cursor: 'pointer' }}
                onFocus={e => (e.target.style.borderColor = T.sage)}
                onBlur={e => (e.target.style.borderColor = T.border)}>
                <option value="">{t('affiliates.apply.channelPlaceholder')}</option>
                {CHANNELS.map(c => (
                  <option key={c} value={c}>{t(`affiliates.apply.channels.${c}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginBottom: 18 }}>
            <div>
              <label style={labelStyle}>{t('affiliates.apply.payoutMethod')}</label>
              <select value={form.payout_method} onChange={set('payout_method')}
                style={{ ...inputStyle, cursor: 'pointer' }}
                onFocus={e => (e.target.style.borderColor = T.sage)}
                onBlur={e => (e.target.style.borderColor = T.border)}>
                <option value="">{t('affiliates.apply.payoutPlaceholder')}</option>
                {PAYOUT_METHODS.map(m => (
                  <option key={m} value={m}>{t(`affiliates.apply.payoutMethods.${m}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={labelStyle}>{t('affiliates.apply.message')}</label>
            <textarea value={form.message} onChange={set('message')} rows={4}
              placeholder={t('affiliates.apply.messagePlaceholder')}
              style={{ ...inputStyle, height: 'auto', padding: '12px 14px', resize: 'vertical', lineHeight: 1.6 }}
              onFocus={e => (e.target.style.borderColor = T.sage)}
              onBlur={e => (e.target.style.borderColor = T.border)} />
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: T.error, fontSize: 14, fontWeight: 600 }}
              >{error}</motion.div>
            )}
          </AnimatePresence>

          <button type="submit" disabled={loading}
            style={{
              width: '100%', padding: '16px', borderRadius: 13,
              background: loading ? T.muted : sageGrad,
              color: T.white, border: 'none', fontWeight: 800, fontSize: 17,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}>
            {loading
              ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> {t('affiliates.apply.submitting')}</>
              : <><Send size={18} /> {t('affiliates.apply.submit')}</>
            }
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: T.muted, marginTop: 16 }}>
            {t('affiliates.apply.reviewNote')}
          </p>
        </form>
      </div>
    </div>
  )
}
